import { Tooltip } from '@material-ui/core';
import React from 'react';
import { IntlShape } from 'react-intl';

import chainMessages from 'components/ApprovalLevelChain/messages';

import { OFFER } from '../types';
import { ApprovalRequest } from './types';

/**
 * Whether the viewer lost the approve permission on the level the server
 * offers them. `actionableStepIds` still names that level, and the decisions
 * endpoint refuses them.
 */
export const viewerLostPermission = (request: ApprovalRequest): boolean => {
  const stepId = request.actionableStepIds[0];
  const ownRow = request.rules
    .flatMap(rule => rule.levels)
    .find(level => level.stepId === stepId)
    ?.approvers.find(approver => approver.isViewer);

  return ownRow?.blockedReason === 'no_permission';
};

/**
 * Why the viewer cannot decide the level the server offers them, or undefined
 * when they can.
 *
 * Approve and Reject show disabled with this reason rather than hidden,
 * because the block is a permission, which `constraint-anti-pattern-guard`
 * keeps visible. It is the sentence the rail prints under the same row.
 */
export const viewerBlockedReason = (
  request: ApprovalRequest,
  intl: IntlShape,
): string | undefined =>
  viewerLostPermission(request)
    ? intl.formatMessage(chainMessages.approverNoPermission, {
        subject: request.subjectType ?? OFFER,
      })
    : undefined;

/**
 * A disabled button fires no pointer or focus events, so the tooltip that says
 * why sits on a focusable wrapper. Renders the child bare with no reason.
 */
export const BlockedTooltip: React.FC<{
  reason?: string;
  children: React.ReactElement;
}> = ({ reason, children }) =>
  reason === undefined ? (
    children
  ) : (
    <Tooltip title={reason}>
      <span tabIndex={0}>{children}</span>
    </Tooltip>
  );
